/**
 * ChatViewWeb.tsx - Web-first chat screen using Firebase Web SDK
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '~contexts/ThemeContext';
import { useUserStore } from '~providers/userStore/zustand';
import FirebaseChatsServiceWeb from '~services/firebase-web/FirebaseChatsServiceWeb';
import ChatWithUserWeb from '~containers/Chats/ChatWithUserWeb/ChatWithUserWeb';
import { ChatTypeWithKey, ConciergeChatTypeWithKey } from '~types/chat';
import { getTimestamp } from '~Utils/Time';
import { initializeFirebaseWeb, getFirestoreWeb, getAuthWeb } from '~services/firebase-web/config';
import { addTestChats } from '~services/firebase-web/addTestData';
import { useContactSupportChat } from '~hooks/chats/useContactSupportChat';
import { collection, getDocs } from 'firebase/firestore';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100vh',
    width: '100%',
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid',
    paddingBottom: '12px',
  },
  headerTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  banner: {
    padding: '12px 16px',
    textAlign: 'center' as const,
    color: 'white',
    fontSize: '13px',
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingBottom: '106px',
  },
  chatItem: {
    display: 'flex',
    padding: '12px 16px',
    borderBottom: '1px solid',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  chatAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    color: 'white',
    fontWeight: '600',
    flexShrink: 0,
  },
  chatInfo: {
    flex: 1,
    minWidth: 0,
  },
  chatName: {
    fontSize: '15px',
    fontWeight: '500',
    marginBottom: '4px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatPreview: {
    fontSize: '13px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid',
    borderTop: '3px solid transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px',
  },
  emptyText: {
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  emptySubtext: {
    fontSize: '12px',
    marginTop: '8px',
    textAlign: 'center' as const,
  },
  addTestDataButton: {
    margin: '8px auto',
    padding: '8px 16px',
    backgroundColor: '#007AFF',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  supportChatButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '12px 16px',
    padding: '16px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    fontSize: '14px',
    fontWeight: '500',
  },
  supportChatIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  supportChatContent: {
    flex: 1,
  },
  supportChatTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '2px',
  },
  supportChatSubtitle: {
    fontSize: '12px',
    opacity: 0.9,
  },
};

export default function ChatViewWeb() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const user = useUserStore(state => state.user);
  const uid = user?.uid;

  const [chats, setChats] = useState<ChatTypeWithKey[]>([]);
  const [chatWithConcierge, setChatWithConcierge] = useState<ConciergeChatTypeWithKey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  
  // Support chat hook
  const { contactChat, isLoading: isSupportChatLoading, createOrFetchSupportChat } = useContactSupportChat();

  const handleChatWithSupport = async () => {
    try {
      console.log('🆘 [SUPPORT] Creating/fetching support chat...');
      const supportChat = await createOrFetchSupportChat();
      
      if (supportChat) {
        console.log('✅ [SUPPORT] Support chat ready, opening:', supportChat.key);
        setSelectedChatId(supportChat.key);
      } else {
        console.error('❌ [SUPPORT] Failed to create/fetch support chat');
        setError('Failed to open support chat');
      }
    } catch (err) {
      console.error('❌ [SUPPORT] Error:', err);
      setError('Failed to open support chat');
    }
  };

  const handleAddTestData = async () => {
    try {
      console.log('📝 handleAddTestData: Starting test data insertion for user:', uid);
      setError(null);
      
      const result = await addTestChats(uid);
      
      console.log('✅ handleAddTestData: Test data result:', result);
      
      if (result.success) {
        console.log('✅ handleAddTestData: Test data added successfully! Chat IDs:', result.chats);
        console.log('📝 handleAddTestData: Data should appear in list via real-time listeners');
      } else {
        console.log('ℹ️ handleAddTestData: Test data already exists:', result.message);
        console.log('ℹ️ handleAddTestData: Existing chat IDs:', result.chats);
      }
    } catch (err) {
      const errorMessage = (err as any).message || String(err);
      console.error('❌ handleAddTestData: Error adding test data:', err);
      console.error('❌ handleAddTestData: Error message:', errorMessage);
      setError(`Failed to add test data: ${errorMessage}`);
    }
  };

  // Monitor network - polling listeners automatically retry
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 [NETWORK] App came online');
      setIsOnline(true);
    };
    const handleOffline = () => {
      console.log('🌐 [NETWORK] App went offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Test Firebase connection immediately
  useEffect(() => {
    console.log('🧪 [FIREBASE-TEST] Starting Firebase connection test...');
    
    try {
      initializeFirebaseWeb();
      console.log('✅ [FIREBASE-TEST] Firebase initialized');
      
      const db = getFirestoreWeb();
      const auth = getAuthWeb();
      console.log('✅ [FIREBASE-TEST] Firestore and Auth instances obtained');
      console.log('🔐 [FIREBASE-TEST] Current user:', auth.currentUser?.uid || 'unauthenticated (allowed by rules)');
      
      // Try to get all chats (no filter) to test connection
      (async () => {
        try {
          const chatsRef = collection(db, 'chats');
          console.log('📋 [FIREBASE-TEST] Getting all chats collection...');
          const snapshot = await getDocs(chatsRef);
          console.log('✅ [FIREBASE-TEST] Firestore connection successful!');
          console.log('📊 [FIREBASE-TEST] Total chats in database:', snapshot.docs.length);
          
          snapshot.docs.forEach((doc, idx) => {
            const data = doc.data();
            console.log(`📄 [FIREBASE-TEST] Chat ${idx + 1}:`, doc.id);
            console.log(`   userIds:`, data.userIds);
            console.log(`   type:`, data.type);
          });
        } catch (testError) {
          console.error('❌ [FIREBASE-TEST] Error querying Firestore:', testError);
        }
      })();
    } catch (err) {
      console.error('❌ [FIREBASE-TEST] Firebase initialization failed:', err);
    }
  }, []);

  // REMOVED DUPLICATE: Second network monitor hook

  // Subscribe to chats
  useEffect(() => {
    if (!uid) {
      setIsLoading(false);
      return;
    }

    console.log('🔥 [WEB] Setting up chat listener');
    setIsLoading(true);
    setError(null);

    const unsubscribe = FirebaseChatsServiceWeb.subscribeToUserChats(
      uid,
      (conversations) => {
        const sorted = conversations.sort((a, b) => {
          const tsA = getTimestamp(a.history);
          const tsB = getTimestamp(b.history);
          return tsB - tsA;
        });
        setChats(sorted);
        setIsLoading(false);
      },
      (err) => {
        console.error('❌ Chat subscription error:', err);
        setError('Failed to load chats');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  // Subscribe to concierge
  useEffect(() => {
    if (!uid) return;

    console.log('🔥 [WEB] Setting up concierge listener');

    const unsubscribe = FirebaseChatsServiceWeb.subscribeToOneOnOneChat(
      uid,
      (chat) => {
        if (chat) {
          console.log('✅ [WEB] Concierge chat found');
          setChatWithConcierge(chat);
        }
      },
      (err) => {
        console.error('❌ Concierge subscription error:', err);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  // Sorted chats (excluding support chats)
  const allChats = useMemo(() => {
    let result: (ChatTypeWithKey | ConciergeChatTypeWithKey)[] = [...chats];
    if (chatWithConcierge) {
      result = [chatWithConcierge, ...result];
    }
    // Filter out support chats (contact-pyxi type) from the list
    result = result.filter((chat) => {
      const isConcierge = 'messages' in chat;
      if (isConcierge) return true; // Keep concierge chats
      return (chat as any).type !== 'contact-pyxi'; // Exclude support chats
    });
    return result.sort((a, b) => {
      const history_a = 'history' in a ? a.history : [];
      const history_b = 'history' in b ? b.history : [];
      const tsA = getTimestamp(history_a);
      const tsB = getTimestamp(history_b);
      return tsB - tsA;
    });
  }, [chats, chatWithConcierge]);

  return (
    <div style={{ ...styles.container, backgroundColor: colors.background }}>
      {selectedChatId ? (
        <ChatWithUserWeb
          chatId={selectedChatId}
          onBack={() => setSelectedChatId(null)}
        />
      ) : (
        <>
          {/* Header */}
          <div style={{ ...styles.header, borderBottomColor: colors.border }}>
            <h1 style={{ ...styles.headerTitle, color: colors.textPrimary }}>
              {t('chat.conversations')}
            </h1>
          </div>

          {/* Offline banner */}
          {!isOnline && (
            <div style={{ ...styles.banner, backgroundColor: colors.warning }}>
              {t('chat.offline_viewing_cached') || 'Offline - Viewing cached conversations'}
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div style={{ ...styles.banner, backgroundColor: colors.error }}>
              {error}
            </div>
          )}

          {/* Loading */}
          {isLoading ? (
            <div style={styles.loadingContainer}>
              <div style={{ ...styles.spinner, borderColor: `${colors.primary}40`, borderTopColor: colors.primary }} />
              <p style={{ color: colors.textSecondary }}>{t('chat.loading_conversations')}</p>
            </div>
          ) : (
            <div style={styles.listContainer}>
              {/* Support Chat Quick Access Button */}
              <button
                style={{
                  ...styles.supportChatButton,
                  backgroundColor: colors.primary,
                  color: 'white',
                  width: '94%',
                  marginBottom: '16px',
                }}
                onClick={handleChatWithSupport}
                disabled={isSupportChatLoading}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                }}
                title="Contact Pyxi Support"
              >
                <div style={{ ...styles.supportChatIcon, backgroundColor: 'white', color: colors.primary }}>
                  💬
                </div>
                <div style={styles.supportChatContent}>
                  <div style={styles.supportChatTitle}>
                    {t('chat.support_chat') || 'Contact Support'}
                  </div>
                  <div style={styles.supportChatSubtitle}>
                    {t('chat.get_help_instantly') || 'Get help from Pyxi team'}
                  </div>
                </div>
                <div style={{ fontSize: '20px', marginLeft: '8px' }}>→</div>
              </button>

              {/* Regular Chats List */}
              {allChats.length > 0 ? (
                allChats.map((chat) => (
                  <ChatItem 
                    key={chat.key} 
                    chat={chat} 
                    colors={colors} 
                    uid={uid}
                    onSelect={() => setSelectedChatId(chat.key)}
                  />
                ))
              ) : (
                <div style={styles.emptyState}>
                  <p style={{ ...styles.emptyText, color: colors.textSecondary }}>
                    {t('chat.no_conversations')}
                  </p>
                  <p style={{ ...styles.emptySubtext, color: colors.textSecondary }}>
                    {t('chat.start_conversation')}
                  </p>
                  <button
                    style={styles.addTestDataButton}
                    onClick={handleAddTestData}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.opacity = '1';
                    }}
                  >
                    Add Test Data (Dev Only)
                  </button>
                </div>
              )}
            </div>
          )}

          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            
            div[style*="chat-item"]:hover {
              opacity: 0.8;
            }
          `}</style>
        </>
      )}
    </div>
  );
}

function ChatItem({ chat, colors, uid, onSelect }: any) {
  const isConcierge = 'messages' in chat;
  const lastMsg = isConcierge
    ? chat.messages?.[chat.messages.length - 1]?.text || ''
    : chat.lastMessage || '';
  
  // Extract display name from chat data - follows ModernChatRow pattern from mobile
  let displayName = 'Unknown';
  let avatarFallback = '?';
  let otherUserImage = null;

  if (isConcierge) {
    displayName = 'Concierge';
    avatarFallback = 'C';
  } else {
    // For event chats, use event name
    if (chat?.eventName) {
      displayName = chat.eventName;
      avatarFallback = chat.eventName?.[0]?.toUpperCase() || 'E';
      otherUserImage = chat?.eventImage;
    } else {
      // For user chats, extract other user's info from chat history or users array
      // This matches the ModernChatRow logic:
      // const otherUserName = otherUserMessage?.sender || chat?.users?.find(user => user !== lastMessage?.sender);
      
      const otherUserId = chat?.userIds?.find((userId: string) => userId !== uid);
      const otherUserMessage = chat?.history?.find((message: any) => message.sender_id === otherUserId);
      const otherUserName = otherUserMessage?.sender;
      otherUserImage = otherUserMessage?.sender_image;

      // Fallback to users array index if no message sender info
      if (!otherUserName && Array.isArray(chat.users)) {
        const userIndex = chat?.userIds?.findIndex((user: string) => user.toLowerCase() !== (uid || '').toLowerCase()) || 0;
        displayName = chat.users[userIndex] || 'Unknown';
      } else {
        displayName = otherUserName || 'Unknown';
      }

      avatarFallback = displayName?.[0]?.toUpperCase() || '?';
    }
  }

  return (
    <div 
      style={{ ...styles.chatItem, borderBottomColor: colors.border, cursor: 'pointer' }}
      onClick={onSelect}
    >
      <div style={{ ...styles.chatAvatar, backgroundColor: otherUserImage ? colors.surface : colors.primary }}>
        {otherUserImage ? (
          <img 
            src={otherUserImage} 
            alt={displayName}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '24px',
              objectFit: 'cover',
            }}
          />
        ) : (
          <span style={{ color: colors.white }}>
            {avatarFallback}
          </span>
        )}
      </div>
      <div style={styles.chatInfo}>
        <div style={{ ...styles.chatName, color: colors.textPrimary }}>{displayName}</div>
        <div style={{ ...styles.chatPreview, color: colors.textSecondary }}>
          {lastMsg.substring(0, 60)}
          {lastMsg.length > 60 ? '...' : ''}
        </div>
      </div>
    </div>
  );
}
